import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import Base64Tool from '../components/Base64Tool';
import CodeBlock from '../components/CodeBlock';
import FaqSection from '../components/Faq';
import { PageHero } from '../components/InfoSections';
import { useTheme } from '../theme';
import { getLanguageGuide, LANGUAGE_GUIDES } from '../lib/snippets';
import { articleSchema, faqPageSchema, breadcrumbSchema, Faq } from '../lib/schema';

export default function LanguagePage({ slug }: { slug: string }) {
  const { themeClasses } = useTheme();
  const guide = getLanguageGuide(slug)!; // routes only mount valid slugs
  const path = `/base64-encode-${guide.slug}`;

  const faqs: Faq[] = [
    {
      q: `How do I Base64 encode a string in ${guide.name}?`,
      a: `Use the code shown above. ${guide.notes}`,
    },
    {
      q: `How do I decode Base64 back to text in ${guide.name}?`,
      a: `Use the decode snippet above. Base64 decoding is lossless, so you get the exact original bytes back; decode them with UTF-8 to recover text.`,
    },
    {
      q: 'Is Base64 encoding the same across programming languages?',
      a: 'Yes. Base64 is a standard (RFC 4648), so a string encoded in one language decodes correctly in any other. Only the API differs, not the output.',
    },
    {
      q: 'Does Base64 secure my data?',
      a: 'No. Base64 is an encoding, not encryption — anyone can decode it. Never use it to protect secrets.',
    },
  ];

  const others = LANGUAGE_GUIDES.filter((g) => g.slug !== guide.slug);

  return (
    <>
      <Seo
        title={`Base64 Encode & Decode in ${guide.name} — Code Examples | EasyBase64`}
        description={`How to Base64 encode and decode in ${guide.name}, with copy-paste code examples and a live converter. UTF-8 safe and free.`}
        path={path}
        jsonLd={[
          articleSchema({
            headline: `Base64 Encode and Decode in ${guide.name}`,
            description: `Code examples for Base64 encoding and decoding in ${guide.name}.`,
            path,
            datePublished: '2026-06-27',
            dateModified: '2026-06-27',
          }),
          faqPageSchema(faqs),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: `Base64 in ${guide.name}`, path },
          ]),
        ]}
      />

      <PageHero
        title={`Base64 in ${guide.name}`}
        subtitle={`How to encode and decode Base64 in ${guide.name} — with copy-paste examples and a live converter to check your output.`}
      />

      <div className="max-w-3xl mx-auto space-y-8">
        <p className={`${themeClasses.textSecondary} leading-relaxed`}>{guide.blurb}</p>

        <section>
          <h2 className={`text-2xl font-bold mb-4 ${themeClasses.textPrimary}`}>{`Encode to Base64 in ${guide.name}`}</h2>
          <CodeBlock code={guide.encode} language={guide.label} />
        </section>

        <section>
          <h2 className={`text-2xl font-bold mb-4 ${themeClasses.textPrimary}`}>{`Decode Base64 in ${guide.name}`}</h2>
          <CodeBlock code={guide.decode} language={guide.label} />
        </section>

        <section className={`${themeClasses.infoCard} rounded-2xl p-5 sm:p-8 border`}>
          <h2 className={`text-xl font-bold mb-3 ${themeClasses.textPrimary}`}>Notes &amp; gotchas</h2>
          <p className={`${themeClasses.textSecondary} leading-relaxed`}>{guide.notes}</p>
        </section>
      </div>

      <div className="my-12">
        <h2 className={`text-2xl font-bold mb-4 text-center ${themeClasses.textPrimary}`}>Try it live</h2>
        <Base64Tool initialMode="encode" variant="standard" />
      </div>

      <div className="max-w-3xl mx-auto mb-12">
        <h2 className={`text-xl font-bold mb-3 ${themeClasses.textPrimary}`}>Base64 in other languages</h2>
        <div className="flex flex-wrap gap-2">
          {others.map((g) => (
            <Link
              key={g.slug}
              to={`/base64-encode-${g.slug}`}
              className={`px-3 py-2 rounded-lg text-sm ${themeClasses.buttonSecondary} border`}
            >
              {g.name}
            </Link>
          ))}
          <Link to="/what-is-base64" className={`px-3 py-2 rounded-lg text-sm ${themeClasses.buttonSecondary} border`}>
            What is Base64?
          </Link>
        </div>
      </div>

      <FaqSection title={`Base64 in ${guide.name} — FAQ`} faqs={faqs} />
    </>
  );
}
